import { useCallback, useEffect, useMemo } from "react";

import moment, { MomentInput } from "moment";

import useURLSearchState from "Hooks/useURLSearchState";

import { getQueryInterval } from "../components/Chart/settings";
import { timeframes as defaultTimeframes } from "../resources/settings";

const isValidInterval = (from, to) => {
  if (moment(from).isAfter(moment()) || moment(to).isAfter(moment()))
    return false;
  if (moment(from).isSameOrAfter(to)) return true;
  return from || to;
};

const isUnderMinimumRange = (from, to, minimumLength) => {
  const diff = moment.unix(to).diff(moment.unix(from), "seconds");
  return diff < minimumLength;
};

const initialState = {
  from: undefined,
  to: undefined,
  range: undefined
};

/**
 * Takes a custom timeframe or an id of a default timeframe.
 * returns current timeframe and a function that sets the value
 */

const useRange = (
  timeframes = defaultTimeframes,
  minimumRangeLength: number | null = null
) => {
  const [state, setState] = useURLSearchState(initialState);

  const setRange = useCallback(
    (range: string | { from: MomentInput; to: MomentInput }) => {
      if (typeof range === "string") {
        setState({
          from: undefined,
          to: undefined,
          range
        });
      } else {
        setState({
          from: moment(range.from).unix(),
          to: moment(range.to).unix(),
          range: undefined
        });
      }
    },
    [setState]
  );

  const range = useMemo(() => {
    if (state.range) {
      return (
        timeframes.find(({ id }) => id?.startsWith(state.range)) ||
        timeframes[0]
      );
    } else if (state.from && state.to) {
      const [from, to] = [
        moment.unix(state.from).toDate(),
        moment.unix(state.to).toDate()
      ];
      if (!isValidInterval(from, to)) {
        return timeframes[0];
      }

      // Extend the query's range to meet minimum requirement
      if (
        minimumRangeLength &&
        isUnderMinimumRange(from, to, minimumRangeLength)
      ) {
        return {
          from,
          to,
          interval: getQueryInterval({ from, to })
        };
      }

      return {
        from,
        to,
        interval: getQueryInterval({ from, to })
      };
    } else {
      return timeframes[0];
    }
  }, [state, timeframes, minimumRangeLength]);

  useEffect(() => {
    const [from, to] = [
      moment.unix(state.from).toDate(),
      moment.unix(state.to).toDate()
    ];
    if (state.from && state.to && !isValidInterval(from, to)) {
      setState({
        from: undefined,
        to: undefined,
        range: timeframes[0].id
      });
    }
  }, [state, setState, timeframes]);

  return { selectedTimeframe: range, setSelectedTimeframe: setRange };
};

export default useRange;
